/**
 * Key generation and import.
 *
 * The private key is always a raw 32-byte Ed25519 scalar. Two provenances are supported:
 *
 *  1. a BIP-39 24-word mnemonic (new, non-custodial users) — the key is derived from the
 *     BIP-39 seed through SLIP-0010 at {@link DERIVATION_PATH};
 *  2. 64 hex characters (existing users, whose custodial "recovery key" is exactly the raw
 *     scalar the server used to hold). Import MUST keep accepting this format forever.
 *
 * Nothing in this file returns a private key to the outside world; the raw bytes stay
 * internal and are handed to the vault/session layer only.
 */

import { ed25519 } from '@noble/curves/ed25519';
import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';

import { toHex, fromHex, wipe } from './bytes';
import { cantonFingerprint, ED25519_KEY_LENGTH } from './identity';
import { InvalidKeyError, InvalidMnemonicError } from './errors';
import { derivePath } from './slip10';

/**
 * SLIP-0010 path used to turn a BIP-39 seed into the Canton signing key.
 *
 * ⚠️ This value is a permanent commitment: changing it silently turns every existing
 * recovery phrase into a different (and therefore wrong) party. Coin type 6767 is a
 * Nuxaris-local choice — Canton has no registered SLIP-44 coin type.
 */
export const DERIVATION_PATH = "m/44'/6767'/0'/0'/0'";

/** Entropy strength for generated mnemonics: 256 bits => 24 words. */
export const MNEMONIC_STRENGTH_BITS = 256;

/** Public (safe to log, store, send) half of an identity. */
export interface WalletIdentity {
  /** Raw Ed25519 public key, 64 hex chars. */
  publicKeyHex: string;
  /** Canton fingerprint, 68 hex chars ("1220" + sha256). */
  fingerprint: string;
}

/** Result of {@link generateWallet} — the mnemonic is shown once, then discarded by the UI. */
export interface GeneratedWallet extends WalletIdentity {
  /** BIP-39 24-word recovery phrase. */
  mnemonic: string;
}

/** Internal: private scalar plus, when it came from a phrase, that phrase. */
export interface KeyMaterial {
  privateKey: Uint8Array;
  mnemonic?: string;
}

/** Normalises a phrase for BIP-39: NFKD, lowercase, single spaces. */
export function normalizeMnemonic(phrase: string): string {
  if (typeof phrase !== 'string') throw new InvalidMnemonicError();
  const normalized = typeof phrase.normalize === 'function' ? phrase.normalize('NFKD') : phrase;
  return normalized.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** True when the phrase is a valid BIP-39 English mnemonic. */
export function isValidMnemonic(phrase: string): boolean {
  try {
    return validateMnemonic(normalizeMnemonic(phrase), wordlist);
  } catch (err) {
    return false;
  }
}

/** True when the string is 64 hex characters (an optional `0x` prefix is tolerated). */
export function isPrivateKeyHex(value: string): boolean {
  return typeof value === 'string' && /^(0x)?[0-9a-fA-F]{64}$/.test(value.trim());
}

function identityFromPrivateKey(privateKey: Uint8Array): WalletIdentity {
  if (!(privateKey instanceof Uint8Array) || privateKey.length !== ED25519_KEY_LENGTH) {
    throw new InvalidKeyError(`Ed25519 private key must be ${ED25519_KEY_LENGTH} raw bytes`);
  }
  const publicKey = ed25519.getPublicKey(privateKey);
  return { publicKeyHex: toHex(publicKey), fingerprint: cantonFingerprint(publicKey) };
}

/** Internal: derives the private scalar from a mnemonic. */
export function privateKeyFromMnemonic(phrase: string): Uint8Array {
  const mnemonic = normalizeMnemonic(phrase);
  if (!validateMnemonic(mnemonic, wordlist)) {
    throw new InvalidMnemonicError('Invalid recovery phrase (unknown word, wrong length or bad checksum)');
  }
  const seed = mnemonicToSeedSync(mnemonic);
  const node = derivePath(DERIVATION_PATH, seed);
  wipe(seed);
  wipe(node.chainCode);
  return node.key;
}

/**
 * Internal: accepts either a 24-word mnemonic or 64 hex chars and returns the raw scalar.
 * This is the single entry point used by `createVault`.
 */
export function keyMaterialFromSecret(secret: string): KeyMaterial {
  if (typeof secret !== 'string' || secret.trim().length === 0) {
    throw new InvalidKeyError('Secret must be a BIP-39 recovery phrase or a 64-character hex private key');
  }
  if (isPrivateKeyHex(secret)) {
    return { privateKey: fromHex(secret, ED25519_KEY_LENGTH) };
  }
  const mnemonic = normalizeMnemonic(secret);
  return { privateKey: privateKeyFromMnemonic(mnemonic), mnemonic };
}

/** Internal: public half of a stored key material blob. */
export function identityFromKeyMaterial(material: KeyMaterial): WalletIdentity {
  return identityFromPrivateKey(material.privateKey);
}

/**
 * Creates a brand new wallet: 24-word BIP-39 mnemonic + the identity it derives to.
 * The private key itself is never returned — persist the mnemonic with `createVault`.
 */
/** A wallet whose secret is the raw key, in recovery-key format: 64 hex characters. */
export interface GeneratedRawWallet extends WalletIdentity {
  privateKeyHex: string;
}

/**
 * Generates a wallet whose secret IS the private key, as 64 hexadecimal characters.
 *
 * This is the historical recovery-key format, and the one the sign-in form expects. No
 * mnemonic, hence no derivation path to pin down: the key is drawn
 * straight from `crypto.getRandomValues`, with nothing in between.
 */
export function generateRawWallet(): GeneratedRawWallet {
  const privateKey = new Uint8Array(ED25519_KEY_LENGTH);
  crypto.getRandomValues(privateKey);
  try {
    const identity = identityFromPrivateKey(privateKey);
    return {
      privateKeyHex: toHex(privateKey),
      publicKeyHex: identity.publicKeyHex,
      fingerprint: identity.fingerprint,
    };
  } finally {
    wipe(privateKey);
  }
}

export function generateWallet(): GeneratedWallet {
  const mnemonic = generateMnemonic(wordlist, MNEMONIC_STRENGTH_BITS);
  const privateKey = privateKeyFromMnemonic(mnemonic);
  try {
    const identity = identityFromPrivateKey(privateKey);
    return { mnemonic, publicKeyHex: identity.publicKeyHex, fingerprint: identity.fingerprint };
  } finally {
    wipe(privateKey);
  }
}

/** Identity of an existing BIP-39 phrase, without persisting anything. */
export function importFromMnemonic(phrase: string): WalletIdentity {
  const privateKey = privateKeyFromMnemonic(phrase);
  try {
    return identityFromPrivateKey(privateKey);
  } finally {
    wipe(privateKey);
  }
}

/**
 * Identity of an existing raw private key (64 hex chars) — the format of the recovery keys
 * handed out while the wallet was custodial.
 */
export function importFromHex(hex64: string): WalletIdentity {
  if (!isPrivateKeyHex(hex64)) {
    throw new InvalidKeyError('Private key must be exactly 64 hexadecimal characters');
  }
  const privateKey = fromHex(hex64, ED25519_KEY_LENGTH);
  try {
    return identityFromPrivateKey(privateKey);
  } finally {
    wipe(privateKey);
  }
}
